import { getItemById } from '../../content/items/items';
import type { CombatState, PlayerState } from '../game/types';
import { learnSkill } from '../progress/progression';
import { applyStatus } from '../combat/statuses';

export interface InventoryStack {
  itemId: string;
  quantity: number;
}

export function getItemQty(inv: readonly InventoryStack[], itemId: string): number {
  return inv.find((stack) => stack.itemId === itemId)?.quantity ?? 0;
}

export function addItem(
  inv: readonly InventoryStack[],
  itemId: string,
  quantity = 1,
): InventoryStack[] {
  if (quantity <= 0) return [...inv];
  const existing = inv.find((stack) => stack.itemId === itemId);
  return existing
    ? inv.map((stack) =>
        stack.itemId === itemId
          ? { ...stack, quantity: stack.quantity + quantity }
          : stack,
      )
    : [...inv, { itemId, quantity }];
}

/** Returns null if the stack does not contain enough items. */
export function consumeItem(
  inv: readonly InventoryStack[],
  itemId: string,
  quantity = 1,
): InventoryStack[] | null {
  if (getItemQty(inv, itemId) < quantity) return null;
  return inv
    .map((stack) =>
      stack.itemId === itemId
        ? { ...stack, quantity: stack.quantity - quantity }
        : stack,
    )
    .filter((stack) => stack.quantity > 0);
}

export type UseItemResult =
  | { ok: false; reason: string }
  | {
      ok: true;
      player: PlayerState;
      combat: CombatState | null;
      requestSave: boolean;
      message: string;
    };

/** Combat items are free actions; books/save items also work in their allowed context. */
export function useInventoryItem(
  player: PlayerState,
  combat: CombatState | null,
  itemId: string,
  inCombat: boolean,
): UseItemResult {
  const def = getItemById(itemId);
  if (!def) return { ok: false, reason: 'Vật phẩm không tồn tại.' };
  if (inCombat && !def.usableInCombat) {
    return { ok: false, reason: 'Không dùng được trong chiến đấu.' };
  }
  if (!inCombat && !def.usableOutOfCombat) {
    return { ok: false, reason: 'Chỉ dùng được trong chiến đấu.' };
  }
  if (
    inCombat &&
    def.effect.type !== 'save_game' &&
    (!combat || combat.playerDefeated || combat.enemyHp <= 0)
  ) {
    return { ok: false, reason: 'Không thể dùng lúc này.' };
  }

  if (def.effect.type === 'learn_skill') {
    const learning = learnSkill(player, def.effect.skillId);
    if (!learning.ok) return { ok: false, reason: learning.message };
    const inventory = consumeItem(player.inventory, itemId);
    if (!inventory) return { ok: false, reason: 'Không còn vật phẩm này.' };
    return {
      ok: true,
      player: { ...learning.player, inventory },
      combat,
      requestSave: false,
      message: `📖 ${def.name}: học được ${learning.skillName}.`,
    };
  }

  const inventory = consumeItem(player.inventory, itemId);
  if (!inventory) return { ok: false, reason: 'Không còn vật phẩm này.' };

  const nextPlayer: PlayerState = { ...player, inventory };
  let nextCombat = combat;
  let requestSave = false;
  let message = `Dùng ${def.name}.`;

  if (def.effect.type === 'save_game') {
    requestSave = true;
    message = `💎 ${def.name}: tiến trình đã được khắc vào pha lê.`;
  } else if (def.effect.type === 'enemy_status' && nextCombat) {
    nextCombat = {
      ...nextCombat,
      enemyStatuses: applyStatus(nextCombat.enemyStatuses, {
        id: def.effect.statusId,
        remainingTurns: def.effect.turns,
        potency: def.effect.potency,
        element: def.effect.element,
      }),
      log: [`🧪 ${def.name} → kẻ địch!`, ...nextCombat.log].slice(0, 8),
    };
    message = `🧪 ${def.name} ảnh hưởng ${nextCombat.enemyName}.`;
  } else if (def.effect.type === 'player_status' && nextCombat) {
    nextCombat = {
      ...nextCombat,
      playerStatuses: applyStatus(nextCombat.playerStatuses, {
        id: def.effect.statusId,
        remainingTurns: def.effect.turns,
        potency: def.effect.potency,
        element: def.effect.element,
      }),
      log: [`🧪 Bạn dùng ${def.name}.`, ...nextCombat.log].slice(0, 8),
    };
    message = `🧪 ${def.name} kích hoạt.`;
  } else {
    return { ok: false, reason: 'Cần đang chiến đấu.' };
  }

  return { ok: true, player: nextPlayer, combat: nextCombat, requestSave, message };
}
